import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import {
  FaArrowRight,
  FaClock,
  FaFileExcel,
  FaFilePdf,
  FaFolderOpen,
  FaProjectDiagram,
  FaSyncAlt,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";
import api from "../../api";
import AppShell from "../../components/AppShell";
import PageHeader from "../../components/PageHeader";
import "../admin/AdminReports.css";

const EMPTY_FILTERS = { startDate: "", endDate: "", status: "all" };
const number = new Intl.NumberFormat("en-ZA", { maximumFractionDigits: 2 });
const integer = new Intl.NumberFormat("en-ZA", { maximumFractionDigits: 0 });

const buildQuery = (filters) => {
  const params = new URLSearchParams();
  if (filters.startDate) params.set("startDate", filters.startDate);
  if (filters.endDate) params.set("endDate", filters.endDate);
  if (filters.status !== "all") params.set("status", filters.status);
  return params.toString();
};

const Metric = ({ label, value, detail, icon: Icon }) => (
  <div className="reports-metric">
    <div>
      <span>{label}</span>
      <strong className="wcco-technical">{value}</strong>
      {detail && <small>{detail}</small>}
    </div>
    <Icon size={18} aria-hidden="true" />
  </div>
);

const PanelHeader = ({ title, description, action }) => (
  <div className="reports-panel-header">
    <div>
      <h2>{title}</h2>
      {description && <p>{description}</p>}
    </div>
    {action}
  </div>
);

const EmptyState = ({ children }) => <div className="reports-empty-state">{children}</div>;

const OrganizationReports = () => {
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState("");
  const [error, setError] = useState("");

  const loadSummary = useCallback(async (filters) => {
    setLoading(true);
    setError("");
    try {
      const query = buildQuery(filters);
      const response = await api.get(`/reports/organization/summary${query ? `?${query}` : ""}`);
      setSummary(response.data);
      setAppliedFilters({ ...filters });
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Unable to load report data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary(EMPTY_FILTERS);
  }, [loadSummary]);

  const download = async (format) => {
    setDownloading(format);
    setError("");
    try {
      const query = buildQuery(appliedFilters);
      const response = await api.get(
        `/reports/organization/export/${format}${query ? `?${query}` : ""}`,
        { responseType: "blob" }
      );
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || `wcco-organisation-report.${format === "excel" ? "xlsx" : "pdf"}`;
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (requestError) {
      setError(requestError.response?.data?.error || `Unable to create the ${format} report.`);
    } finally {
      setDownloading("");
    }
  };

  const updateFilter = (event) => {
    const { name, value } = event.target;
    setDraftFilters((current) => ({ ...current, [name]: value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    if (draftFilters.startDate && draftFilters.endDate && draftFilters.startDate > draftFilters.endDate) {
      setError("The activity start date cannot be after the end date.");
      return;
    }
    loadSummary(draftFilters);
  };

  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    loadSummary(EMPTY_FILTERS);
  };

  const report = useMemo(() => {
    if (!summary) return null;
    const { metrics, students = [], projects = [], distributions } = summary;
    const totalHours = metrics.approvedHours + metrics.pendingHours + metrics.rejectedHours;
    const hourStatuses = [
      { label: "Approved", value: metrics.approvedHours },
      { label: "Pending", value: metrics.pendingHours },
      { label: "Rejected", value: metrics.rejectedHours },
    ].map((item) => ({
      ...item,
      share: totalHours ? (item.value / totalHours) * 100 : 0,
    }));
    const maxCategoryHours = Math.max(
      0,
      ...(distributions.categories || []).map((category) => Number(category.approvedHours) || 0)
    );

    return {
      metrics,
      students,
      projects,
      categories: distributions.categories || [],
      hourStatuses,
      totalHours,
      maxCategoryHours,
    };
  }, [summary]);

  const hasAppliedFilters = Boolean(
    appliedFilters.startDate || appliedFilters.endDate || appliedFilters.status !== "all"
  );
  const hasDraftFilters = Boolean(
    draftFilters.startDate || draftFilters.endDate || draftFilters.status !== "all"
  );

  const periodLabel = hasAppliedFilters
    ? [
        appliedFilters.startDate || "Beginning",
        appliedFilters.endDate || "Today",
        appliedFilters.status === "all" ? "all statuses" : `${appliedFilters.status} logs`,
      ].join(" · ")
    : "All-time activity · all statuses";

  const generatedAt = summary?.generatedAt
    ? new Date(summary.generatedAt).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })
    : "";

  return (
    <AppShell>
      <PageHeader
        title="Organisation reports"
        description={`Analyse activity across your posted projects, then export the same applied dataset${generatedAt ? ` · Updated ${generatedAt}` : ""}.`}
        actions={
          <>
            <Button
              variant="outline-primary"
              onClick={() => download("pdf")}
              disabled={Boolean(downloading) || loading || !summary}
            >
              {downloading === "pdf" ? <Spinner size="sm" className="me-2" /> : <FaFilePdf className="me-2" />}
              Export PDF
            </Button>
            <Button
              onClick={() => download("excel")}
              disabled={Boolean(downloading) || loading || !summary}
            >
              {downloading === "excel" ? <Spinner size="sm" className="me-2" /> : <FaFileExcel className="me-2" />}
              Export Excel
            </Button>
          </>
        }
      />

      {error && <Alert>{error}</Alert>}

      <section className="reports-filter-panel">
        <PanelHeader
          title="Report filters"
          description="Date filters affect hour-log activity. Project inventory totals remain all-time."
          action={<span className="wcco-status">{periodLabel}</span>}
        />

        <Form onSubmit={applyFilters}>
          <div className="reports-filter-grid">
            <Form.Group>
              <Form.Label>Activity start date</Form.Label>
              <Form.Control
                type="date"
                name="startDate"
                value={draftFilters.startDate}
                max={draftFilters.endDate || undefined}
                onChange={updateFilter}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Activity end date</Form.Label>
              <Form.Control
                type="date"
                name="endDate"
                value={draftFilters.endDate}
                min={draftFilters.startDate || undefined}
                onChange={updateFilter}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Hour-log status</Form.Label>
              <Form.Select name="status" value={draftFilters.status} onChange={updateFilter}>
                <option value="all">All statuses</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </Form.Select>
            </Form.Group>
            <div className="reports-filter-actions">
              <Button type="submit" disabled={loading}>
                {loading ? <Spinner size="sm" className="me-2" /> : <FaSyncAlt className="me-2" aria-hidden="true" />}
                Apply filters
              </Button>
              <Button
                type="button"
                variant="outline-secondary"
                onClick={clearFilters}
                disabled={loading || (!hasAppliedFilters && !hasDraftFilters)}
              >
                Reset
              </Button>
            </div>
          </div>
        </Form>
      </section>

      {loading ? (
        <div className="reports-loading" role="status">
          <Spinner animation="border" />
          <span>Loading report data…</span>
        </div>
      ) : report && (
        <>
          <section className="reports-metrics" aria-label="Report summary">
            <Metric label="Volunteers" value={integer.format(report.metrics.totalStudents)} detail="engaged across your projects" icon={FaUsers} />
            <Metric label="Projects" value={integer.format(report.metrics.totalProjects)} detail={`${report.metrics.pendingProjects} awaiting admin review`} icon={FaProjectDiagram} />
            <Metric label="Active volunteers" value={integer.format(report.metrics.activeVolunteers)} detail="with logged activity" icon={FaUserCheck} />
            <Metric label="Approved hours" value={number.format(report.metrics.approvedHours)} detail="accepted contribution" icon={FaClock} />
            <Metric label="Pending hours" value={number.format(report.metrics.pendingHours)} detail="awaiting review" icon={FaFolderOpen} />
            <Metric label="Rejected hours" value={number.format(report.metrics.rejectedHours)} detail="not included in totals" icon={FaClock} />
          </section>

          <Row className="g-4 mb-4">
            <Col xl={7}>
              <section className="reports-panel">
                <PanelHeader
                  title="Top volunteer activity"
                  description="Volunteers ranked by approved hours in the applied period."
                  action={<span className="wcco-status">Top 10</span>}
                />
                {report.students.length ? (
                  <div className="table-responsive">
                    <Table hover>
                      <thead>
                        <tr>
                          <th className="reports-rank-column">Rank</th>
                          <th>Volunteer</th>
                          <th>Faculty</th>
                          <th className="text-end">Projects</th>
                          <th className="text-end">Approved hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.students.slice(0, 10).map((student, index) => (
                          <tr key={`${student.studentNumber}-${student.email}`}>
                            <td className="wcco-technical">{index + 1}</td>
                            <td>
                              {student.name}
                              <small>{student.studentNumber || student.email}</small>
                            </td>
                            <td>{student.faculty}</td>
                            <td className="text-end wcco-technical">{student.projects}</td>
                            <td className="text-end wcco-technical">{number.format(student.approvedHours)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <EmptyState>No volunteer activity matches the applied filters.</EmptyState>
                )}
              </section>
            </Col>

            <Col xl={5}>
              <section className="reports-panel">
                <PanelHeader
                  title="Hour-log outcomes"
                  description="How submitted hours are distributed across review states."
                  action={<span className="wcco-status">{number.format(report.totalHours)} total hrs</span>}
                />
                <ol className="reports-status-list">
                  {report.hourStatuses.map((status) => (
                    <li key={status.label}>
                      <div>
                        <span>{status.label}</span>
                        <strong className="wcco-technical">{number.format(status.value)} hrs</strong>
                      </div>
                      <div className="reports-track" aria-hidden="true">
                        <span style={{ width: `${status.share}%` }} />
                      </div>
                      <small>{number.format(status.share)}% of logged hours</small>
                    </li>
                  ))}
                </ol>
              </section>
            </Col>
          </Row>

          <Row className="g-4 pb-4">
            <Col xl={7}>
              <section className="reports-panel">
                <PanelHeader
                  title="Project contribution"
                  description="Your projects ranked by approved volunteer hours."
                  action={<span className="wcco-status">Top 10</span>}
                />
                {report.projects.length ? (
                  <div className="table-responsive">
                    <Table hover>
                      <thead>
                        <tr>
                          <th>Project</th>
                          <th>Status</th>
                          <th className="text-end">Volunteers</th>
                          <th className="text-end">Approved hours</th>
                          <th className="text-end">Pending hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.projects.slice(0, 10).map((project) => (
                          <tr key={`${project.name}-${project.organiser}`}>
                            <td>
                              {project.name}
                              <small>{project.categories}</small>
                            </td>
                            <td>{project.approved ? "Approved" : "Pending"}</td>
                            <td className="text-end wcco-technical">{project.volunteers}</td>
                            <td className="text-end wcco-technical">{number.format(project.approvedHours)}</td>
                            <td className="text-end wcco-technical">{number.format(project.pendingHours)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <EmptyState>No projects are available.</EmptyState>
                )}
              </section>
            </Col>

            <Col xl={5}>
              <section className="reports-panel">
                <PanelHeader
                  title="Project categories"
                  description="Approved contribution and project count by category."
                  action={<span className="wcco-status">{report.categories.length} categories</span>}
                />
                {report.categories.length ? (
                  <ol className="reports-category-list">
                    {report.categories.slice(0, 10).map((category) => {
                      const approvedHours = Number(category.approvedHours) || 0;
                      const width = report.maxCategoryHours ? (approvedHours / report.maxCategoryHours) * 100 : 0;
                      return (
                        <li key={category.name}>
                          <div>
                            <span>{category.name}</span>
                            <small>{category.projects} {category.projects === 1 ? "project" : "projects"}</small>
                          </div>
                          <div className="reports-category-value">
                            <div className="reports-track" aria-hidden="true"><span style={{ width: `${width}%` }} /></div>
                            <strong className="wcco-technical">{number.format(category.approvedHours)} hrs</strong>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                ) : (
                  <EmptyState>No project categories are available.</EmptyState>
                )}
                {report.categories.length > 10 && (
                  <div className="reports-panel-footer">
                    <span>Showing the ten highest-volume categories.</span>
                    <Button variant="link" onClick={() => download("excel")} disabled={Boolean(downloading)}>
                      Export all <FaArrowRight className="ms-2" aria-hidden="true" />
                    </Button>
                  </div>
                )}
              </section>
            </Col>
          </Row>
        </>
      )}
    </AppShell>
  );
};

export default OrganizationReports;
